/**
 * Simple Language Toggle for Pythagoras Trees
 * A minimal single-button approach that cycles through available languages
 */

import { i18n, t } from './i18n.js';
import { domCache } from './dom-cache.js';

/**
 * Language toggle component - cycles through languages with a single button
 */
export class LanguageToggle {
    constructor() {
        this.toggleButton = null;
        this.isInitialized = false;
        this.languages = [];
        this.currentIndex = 0;

        // Bind event handlers
        this.handleClick = this.handleClick.bind(this);
        this.handleLanguageChange = this.handleLanguageChange.bind(this);
    }

    /**
     * Initialize the language toggle
     */
    async initialize() {
        if (this.isInitialized) {
            return;
        }

        try {
            // Wait for i18n to be ready
            if (!i18n.isInitialized) {
                await i18n.initialize();
            }

            // Get supported languages and find current index
            this.languages = i18n.getSupportedLanguages();
            this.currentIndex = this.languages.indexOf(i18n.getCurrentLanguage());
            if (this.currentIndex === -1) {
                this.currentIndex = 0;
            }

            // Create the toggle button
            this.createToggleButton();

            // Register for language change events
            i18n.onLanguageChange(this.handleLanguageChange);

            this.isInitialized = true;
        } catch (error) {
            console.error('Failed to initialize language toggle:', error);
        }
    }

    /**
     * Create the toggle button UI element
     */
    createToggleButton() {
        // Create the button container
        const container = document.createElement('div');
        container.className = 'language-toggle-wrapper';

        // Create the toggle button
        this.toggleButton = document.createElement('button');
        this.toggleButton.className = 'language-toggle';
        this.toggleButton.id = 'languageToggle';
        this.toggleButton.setAttribute('aria-label', 'Cycle through languages');
        this.toggleButton.title = 'Click to change language';

        // Create button content
        const langCode = document.createElement('span');
        langCode.className = 'lang-code';
        langCode.textContent = this.getCurrentLanguageCode();

        const toggleHint = document.createElement('span');
        toggleHint.className = 'toggle-hint';
        toggleHint.textContent = '⚡';
        toggleHint.setAttribute('aria-hidden', 'true');

        // Assemble button
        this.toggleButton.appendChild(langCode);
        this.toggleButton.appendChild(toggleHint);
        container.appendChild(this.toggleButton);

        // Add click handler
        this.toggleButton.addEventListener('click', this.handleClick);

        // Add to panel
        this.addToPanel(container);
    }

    /**
     * Get current language code in uppercase
     */
    getCurrentLanguageCode() {
        return i18n.getCurrentLanguage().toUpperCase();
    }

    /**
     * Handle button click - cycle to next language
     */
    async handleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        // Show loading state
        this.setLoadingState(true);

        try {
            // Move to next language
            this.currentIndex = (this.currentIndex + 1) % this.languages.length;
            const nextLang = this.languages[this.currentIndex];

            // Change language
            const success = await i18n.changeLanguage(nextLang);

            if (success) {
                // Language changed successfully, nothing more to do
            } else {
                console.warn(`Failed to toggle to language: ${nextLang}`);
                // Revert index on failure
                this.currentIndex = this.languages.indexOf(i18n.getCurrentLanguage());
            }
        } catch (error) {
            console.error('Error toggling language:', error);
            // Revert index on error
            this.currentIndex = this.languages.indexOf(i18n.getCurrentLanguage());
        } finally {
            this.setLoadingState(false);
        }
    }

    /**
     * Handle language change events (from other sources)
     */
    handleLanguageChange(newLang, oldLang) {
        // Update current index
        this.currentIndex = this.languages.indexOf(newLang);
        if (this.currentIndex === -1) {
            this.currentIndex = 0;
        }

        // Update button display
        this.updateButtonDisplay();
    }

    /**
     * Update button display
     */
    updateButtonDisplay() {
        if (!this.toggleButton) {
            return;
        }

        const langCodeSpan = this.toggleButton.querySelector('.lang-code');
        if (langCodeSpan) {
            langCodeSpan.textContent = this.getCurrentLanguageCode();
        }

        // Update tooltip with next language info
        const nextIndex = (this.currentIndex + 1) % this.languages.length;
        const nextLang = this.languages[nextIndex];
        const nextLangName = i18n.getLanguageName(nextLang);
        this.toggleButton.title = `Current: ${i18n.getLanguageName(i18n.getCurrentLanguage())} | Click for: ${nextLangName}`;
    }

    /**
     * Set loading state
     */
    setLoadingState(isLoading) {
        if (!this.toggleButton) {
            return;
        }

        if (isLoading) {
            this.toggleButton.disabled = true;
            this.toggleButton.classList.add('loading');

            const langCodeSpan = this.toggleButton.querySelector('.lang-code');
            if (langCodeSpan) {
                langCodeSpan.dataset.original = langCodeSpan.textContent;
                langCodeSpan.textContent = '...';
            }
        } else {
            this.toggleButton.disabled = false;
            this.toggleButton.classList.remove('loading');

            const langCodeSpan = this.toggleButton.querySelector('.lang-code');
            if (langCodeSpan && langCodeSpan.dataset.original) {
                langCodeSpan.textContent = langCodeSpan.dataset.original;
                delete langCodeSpan.dataset.original;
            }

            this.updateButtonDisplay();
        }
    }

    /**
     * Add toggle to panel
     */
    addToPanel(container) {
        // Try to add to panel header
        const panelHeader = domCache.getWithFallback('panel-header', '.panel-header', false);

        if (panelHeader) {
            // Insert before the toggle button or append to header
            const toggleBtn = panelHeader.querySelector('#panel-toggle, .panel-toggle-btn');
            if (toggleBtn) {
                panelHeader.insertBefore(container, toggleBtn);
            } else {
                panelHeader.appendChild(container);
            }
        } else {
            console.warn('Could not find panel header, language toggle not added to UI');
        }
    }

    /**
     * Get debug information
     */
    getDebugInfo() {
        return {
            isInitialized: this.isInitialized,
            languages: this.languages,
            currentIndex: this.currentIndex,
            currentLanguage: i18n.getCurrentLanguage(),
            hasButton: !!this.toggleButton,
        };
    }

    /**
     * Cleanup
     */
    destroy() {
        if (this.toggleButton) {
            this.toggleButton.removeEventListener('click', this.handleClick);
            this.toggleButton.remove();
            this.toggleButton = null;
        }

        i18n.offLanguageChange(this.handleLanguageChange);
        this.isInitialized = false;
    }
}

// Create singleton instance
export const languageToggle = new LanguageToggle();

// Export for global access if needed
if (typeof window !== 'undefined') {
    window.languageToggle = languageToggle;
}
